/**
 * What the installed JsSIP can do for video
 *
 * My camera on a call needs @per_moeller/jssip-telecomx 4.13.0: a failed re-INVITE that keeps the call
 * (terminateOnFailure), ICE candidates rewritten per m-section (forceUseStunResponse on the video m-line too), a video
 * transport failing ICE without ending the call, and a retry after 491. With an older JsSIP a failed camera re-INVITE
 * ends the whole call, so the camera is not offered at all - the other party's video still shows when they send it.
 */

import JsSIP from '@per_moeller/jssip-telecomx'

/** The first JsSIP release my camera can be used with */
export const VIDEO_JSSIP_VERSION = '4.13.0'

/**
 * The numbers of a version: major, minor and patch - a missing patch is 0, anything after the numbers is ignored
 * @param version The version, e.g. '4.12.0'
 * @returns The three numbers, undefined when it isn't a version
 */
function versionNumbers(version: unknown): [ number, number, number ] | undefined {
	if (typeof version !== 'string') { return undefined }

	const match = /^\s*v?(\d+)\.(\d+)(?:\.(\d+))?/.exec(version)

	if (!match) { return undefined }
	return [ Number(match[1]), Number(match[2]), Number(match[3] ?? 0) ]
}

/**
 * Whether a version is a minimum version or later, compared part by part as numbers - so 4.9.0 is older than 4.13.0
 * @param version The version - missing or not a version counts as too old
 * @param minimum The minimum version
 */
export function isVersionAtLeast(version: unknown, minimum: string): boolean {
	const have = versionNumbers(version)
	const need = versionNumbers(minimum)

	if (!have || !need) { return false }

	for (let part = 0; part < need.length; part++) {
		if (have[part] !== need[part]) { return (have[part] ?? 0) > (need[part] ?? 0) }
	}
	return true
}

/** Whether my camera can be used on a call with the installed JsSIP - 4.13.0 or later, see VIDEO_JSSIP_VERSION */
export function jssipSupportsVideo(): boolean {
	// Read defensively: a JsSIP that doesn't report its version is taken as too old
	return isVersionAtLeast((JsSIP as { version?: unknown } | undefined)?.version, VIDEO_JSSIP_VERSION)
}
